import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { fromJS, Set as ISet, Map as IMap } from 'immutable';
import { LoadSpinner } from 'cstar-react-primitives/lib/LoadSpinner';

import { getNewItemFormValues, newItemIsPlastic, newItemIsSelfBuy } from '../item/newItemForm';
import { saveNewItemAction } from '../item/itemModule';
import NotFound from '../error/NotFound';
import { logError } from '../utils/errorUtils';
import { uyoSelectStepCompleted } from '../uyo/select/uyoSelectStepSelectors';
import { newItemIsAccessoriesStepForSelfBuy } from '../accessories/accessoriesUtils';

import Redirect from './Redirect';
import { push } from './routing';
import {
    getFlowConfig,
    getPathStepConfig,
    isPreCartFlowSkipped
} from './flowSelectors';
import {
    getBasePath,
    getNextPath,
    getNextPathSubmitMessage,
    isUYOStep
} from './flow';
import { redirectIfDataRequirementsUnsatisfied } from './stepUtils';
import { changeStep } from './flowModule';
import { updateFlowProgress } from './flowProgressModule';
import flowMessages from './flowMessages';
import { setCartViewed } from '../cart/cartModule';
import { parseCartViewed } from '../segment/segmentSelectors';
import { getShowAppLoadingSpinner } from '../app/appSelectors';



export class AsyncFlowStep extends React.Component {
    constructor(props) {
        super(props);

        const { component } = this.props;

        this.state = {
            Component: React.lazy(component)
        };

        this.getChildProps = this.getChildProps.bind(this);
        this.getSubmitAction = this.getSubmitAction.bind(this);
        this.handleStepChange = this.handleStepChange.bind(this);
    }

    componentDidMount() {
        const {
            loadNextStep,
            beforeRender,
            dispatch
        } = this.props;

        if (loadNextStep) {
            
            
            
            loadNextStep();
        }

        beforeRender(dispatch, this.getChildProps());

        
        
        this.handleStepChange();
    }

    componentDidUpdate(prevProps) {
        const {
            isReadyToRender,
            isAllowedToRender,
            match
        } = this.props;

        
        
        if (isReadyToRender !== prevProps.isReadyToRender
                || isAllowedToRender !== prevProps.isAllowedToRender
                || match.url !== prevProps.match.url) {
            this.handleStepChange();
        }
    }

    
    getChildProps() {
        const {
            component, path, flowProps, beforeRender, isReady, canLoad, onExit,
            isReadyToRender, isAllowedToRender, redirectPath, dispatch, loadNextStep,
            ...childProps
        } = this.props;

        return childProps;
    }

    
    getSubmitAction() {
        const {
            submitPath,
            cartViewedSegment,
            onExit,
            dispatch
        } = this.props;

        return (values) => {
            onExit(dispatch, this.getChildProps(), values);
            if (values) {
                try {
                    dispatch(updateFlowProgress(fromJS(values).keySeq()));
                
                } catch (err) {
                    
                    
                    
                }
            }

            if (submitPath === null) {
                
                
                dispatch(saveNewItemAction(values));
                dispatch(setCartViewed(cartViewedSegment));
            } else {
                push({ pathname: submitPath });
            }
        };
    }

    handleStepChange() {
        const {
            path,
            dispatch,
            isReadyToRender,
            isAllowedToRender,
            location,
            match
        } = this.props;

        
        const payloadSessionID = location.state ? location.state.sessionID : null;

        if (isReadyToRender && isAllowedToRender) {
            
            const stepPath = path.replace(/\/:(\w+)/gi, (regexMatch, p1) => `/${match.params[p1]}`);

            dispatch(changeStep(stepPath, payloadSessionID));
        }
    }

    render() {
        const { Component } = this.state;
        const {
            path,
            isReadyToRender,
            isAllowedToRender,
            redirectPath,
            flowProgressUpdates,
            dispatch,
            submitValues,
            replaceHistory,
            loading
        } = this.props;

        if (isReadyToRender) {
            if (isAllowedToRender) {
                return <Component {...this.getChildProps()} submitAction={this.getSubmitAction()} />;
            }

            if (!isAllowedToRender) {
                if (!redirectPath) {
                    if (submitValues && submitValues !== {}) {
                        dispatch(saveNewItemAction(submitValues, replaceHistory));
                        return !loading ? <LoadSpinner /> : null;
                    }
                    
                    logError(`step for path /store/${path} missing redirectPath when not allowed to render!`);
                    return <NotFound />;
                }

                if (flowProgressUpdates) {
                    dispatch(updateFlowProgress(flowProgressUpdates));
                }
                return <Redirect pathname={redirectPath} />;
            }
        }

        return null;
    }
}

AsyncFlowStep.defaultProps = {
    flow: null,
    submitPath: null,
    isReadyToRender: false,
    isAllowedToRender: false,
    redirectPath: null,
    dispatch: () => {},
    loadNextStep: null,
    flowProgressUpdates: null,
    submitValues: null,
    replaceHistory: false,
    cartViewedSegment: null,
    loading: false
};

AsyncFlowStep.propTypes = {
    
    component: PropTypes.func.isRequired,
    flow: PropTypes.string,
    path: PropTypes.string.isRequired,
    submitPath: PropTypes.string,
    location: PropTypes.object.isRequired,
    match: PropTypes.object.isRequired,
    
    flowProps: PropTypes.func.isRequired,
    beforeRender: PropTypes.func.isRequired,
    isReady: PropTypes.func.isRequired,
    canLoad: PropTypes.func.isRequired,
    canSkip: PropTypes.func.isRequired,
    onExit: PropTypes.func.isRequired,
    
    isReadyToRender: PropTypes.bool,
    isAllowedToRender: PropTypes.bool,
    redirectPath: PropTypes.string,
    loadNextStep: PropTypes.func,
    flowProgressUpdates: PropTypes.instanceOf(ISet),
    submitValues: PropTypes.instanceOf(IMap),
    replaceHistory: PropTypes.bool,
    
    dispatch: PropTypes.func,
    cartViewedSegment: PropTypes.object,
    loading: PropTypes.bool
};

export const mapStateToProps = (state, ownProps) => {
    const {
        flow,
        path,
        flowProps,
        isReady,
        canLoad,
        canSkip
    } = ownProps;
    const cartViewedSegment = parseCartViewed(state);
    const isSelfBuy = newItemIsSelfBuy(state);
    const isPlastic = newItemIsPlastic(state);
    const isUYO = uyoSelectStepCompleted(state) || isUYOStep(path);
    const hasAccessories = newItemIsAccessoriesStepForSelfBuy(state);
    const flowConfig = flow ? getFlowConfig(state).get(flow) : null;
    const loading = getShowAppLoadingSpinner(state);

    let nextPath;
    let submitPath;
    let loadNextStep;
    const branchFlags = {
        isSelfBuy,
        isPlastic,
        isUYO,
        hasAccessories
    };
    if (flow) {
        nextPath = getNextPath(flowConfig, path, branchFlags);

        if (nextPath) {
            submitPath = `/${getBasePath()}/${nextPath}`;

            
            loadNextStep = getPathStepConfig(state)(nextPath).get('component');
        }
    }

    const commonFlowProps = {
        isSelfBuy,
        isPlastic,
        submitPath,
        loading
    };

    
    const propsFromFlow = flowProps(state, {
        ...commonFlowProps,
        ...ownProps
    });

    const mergedProps = {
        ...ownProps,
        ...commonFlowProps,
        ...propsFromFlow
    };

    let isAllowedToRender = false;
    let redirectPath = null;
    let flowProgressUpdates = null;
    let getSubmitMessage = null;

    
    const isReadyToRender = isReady(state, mergedProps);

    
    if (isReadyToRender) {
        
        if (canSkip(state)) {
            if (!submitPath) {
                const submitValues = getNewItemFormValues(state);
                const replaceHistory = isPreCartFlowSkipped(state);
                return {
                    ...mergedProps,
                    isReadyToRender,
                    isAllowedToRender: false,
                    redirectPath: submitPath,
                    flowProgressUpdates,
                    loadNextStep,
                    getSubmitMessage,
                    submitValues,
                    replaceHistory
                };
            }
            return {
                ...mergedProps,
                isReadyToRender,
                isAllowedToRender: false,
                redirectPath: submitPath,
                flowProgressUpdates,
                loadNextStep,
                getSubmitMessage
            };
        }

        const { isAllowedOrPath, flowProgress } = canLoad(state, mergedProps);
        flowProgressUpdates = flowProgress;

        if (flow) {
            getSubmitMessage = getNextPathSubmitMessage(state, flow, flowConfig, path, branchFlags);
            redirectPath = redirectIfDataRequirementsUnsatisfied(state, flow, path, mergedProps);
        } else {
            getSubmitMessage = () => flowMessages.genericSubmitButtonText;
        }

        if (redirectPath === null) {
            if (typeof isAllowedOrPath === 'string' || typeof isAllowedOrPath === 'undefined') {
                redirectPath = isAllowedOrPath;
            } else {
                isAllowedToRender = true;
            }
        }
    }
    return {
        ...mergedProps,
        isReadyToRender,
        isAllowedToRender,
        redirectPath,
        flowProgressUpdates,
        loadNextStep,
        getSubmitMessage,
        cartViewedSegment
    };
};

export default connect(mapStateToProps)(AsyncFlowStep);
